import { KINDS, MAX_LEVEL, SEAL, SUMMON_WEIGHTS, TIMING, TOTEM_KINDS, TOTEM_STATS, WILD, mergeCharge, type Kind, type TotemKind } from './config.ts';
import { DT, type Ctx } from './context.ts';
import { touchCell } from './combat.ts';
import { int, random, type RngState } from './rng.ts';
import type { Cell, PlayerState } from './state.ts';

export const summonCost = (p: PlayerState) => Math.min(SEAL.maxCost, SEAL.baseCost + p.summons);

/** A summon's element, drawn from the player's deck (all kinds when none is given) by SUMMON_WEIGHTS. */
export function rollKind(rng: RngState, deck: readonly Kind[] = KINDS): Kind {
  let total = 0; for (const k of deck) total += SUMMON_WEIGHTS[k];
  let x = random(rng) * total;
  for (const k of deck) { x -= SUMMON_WEIGHTS[k]; if (x <= 0) return k; }
  return deck[deck.length - 1];
}

export const emptyCell = (): Cell => ({ phase: 'empty', kind: 'r', level: 0, timer: 0, version: 0, cd: 0, castTarget: -1, castLeft: 0 });

/** A relic placed on the board: its first shot comes after a random part of a second. */
export function placeRelic(rng: RngState, cell: Cell, kind: Kind, level: number) {
  cell.phase = 'idle'; cell.kind = kind; cell.level = level; cell.timer = 0; cell.version++;
  cell.cd = random(rng) * 0.8; cell.castTarget = -1; cell.castLeft = 0;
}

export function canMerge(a: Cell, b: Cell) {
  return a !== b && a.phase === 'idle' && b.phase === 'idle' && a.level === b.level && a.level < MAX_LEVEL
    && (a.kind === b.kind || a.kind === WILD || b.kind === WILD);
}
/** What a merge produces: the element of the pair, the prism taking the other's (two prisms stay a prism). */
export const mergedKind = (a: Cell, b: Cell): Kind => (a.kind === WILD ? b.kind : a.kind);

export function summon(ctx: Ctx, player: number) {
  const { st } = ctx, p = st.players[player];
  const empty: number[] = [];
  p.cells.forEach((c, i) => { if (c.phase === 'empty') empty.push(i); });
  const cost = summonCost(p);
  if (!empty.length || p.sp < cost) { ctx.ev.push({ type: 'summon_denied', player, reason: !empty.length ? 'full' : 'energy' }); return; }
  p.sp -= cost; p.summons++;
  // the result is chosen only after a successful summon, with no forecast queue
  const index = empty[int(st.rng, empty.length)], kind = rollKind(st.rng, p.deck), cell = p.cells[index];
  cell.phase = 'incoming'; cell.kind = kind; cell.level = 1; cell.timer = TIMING.summonFlight; cell.version++;
  ctx.ev.push({ type: 'summon', player, cell: index, kind, flight: TIMING.summonFlight });
}

export function merge(ctx: Ctx, player: number, from: number, to: number) {
  const p = ctx.st.players[player], a = p.cells[from], b = p.cells[to];
  if (!a || !b || !canMerge(a, b)) { ctx.ev.push({ type: 'merge_denied', player, from, to }); return; }
  const kind = mergedKind(a, b), level = a.level + 1, charge = mergeCharge(level);
  touchCell(ctx, player, from);
  a.phase = 'empty'; a.level = 0; a.castTarget = -1;
  touchCell(ctx, player, to);
  // the target keeps its attack rhythm; it fights again once the charge releases
  b.phase = 'merging'; b.kind = kind; b.level = level; b.timer = charge + 0.02;
  ctx.ev.push({ type: 'merge', player, from, to, kind, level, charge });
}

/**
 * The computer ally builds its own field from nothing, as a player would: every third of a second it
 * merges a pair if it has one, otherwise summons when its seal allows. (Its kills charge the human's
 * seal; its own fills from the trickle alone.)
 */
export function botTurn(ctx: Ctx, player: number) {
  const p = ctx.st.players[player];
  if ((ctx.st.tick + player * 7) % 20) return;
  for (let a = 0; a < p.cells.length; a++) for (let b = 0; b < p.cells.length; b++) {
    if (a !== b && canMerge(p.cells[a], p.cells[b])) { merge(ctx, player, a, b); return; }
  }
  if (p.sp >= summonCost(p) && p.cells.some((c) => c.phase === 'empty')) summon(ctx, player);
}

export function equipTotem(ctx: Ctx, player: number, kind: TotemKind) {
  const p = ctx.st.players[player];
  if (!TOTEM_KINDS.includes(kind) || kind === p.totem) return;
  p.totem = kind; p.totemCharge = 0; p.totemVersion++;
  ctx.ev.push({ type: 'totem', player, kind });
}

/** The ascend totem: one random idle relic of the lowest level rises a level; the charge waits for a target. */
function ascend(ctx: Ctx, player: number) {
  const p = ctx.st.players[player];
  let low = MAX_LEVEL;
  for (const c of p.cells) if (c.phase === 'idle' && c.level < low) low = c.level;
  if (low >= MAX_LEVEL) return;
  const pool: number[] = [];
  p.cells.forEach((c, i) => { if (c.phase === 'idle' && c.level === low) pool.push(i); });
  if (!pool.length) return;
  p.totemCharge = 0;
  const index = pool[int(ctx.st.rng, pool.length)], cell = p.cells[index];
  touchCell(ctx, player, index);
  cell.phase = 'ascending'; cell.level++; cell.timer = TIMING.ascendRite;
  ctx.ev.push({ type: 'ascend', player, cell: index, kind: cell.kind, level: cell.level, rite: TIMING.ascendRite });
}

/** The shatter totem, charged: the sphere in `index` breaks and its well is free; the charge starts over. */
export function shatter(ctx: Ctx, player: number, index: number) {
  const p = ctx.st.players[player], cell = p.cells[index];
  if (p.totem !== 'shatter' || p.totemCharge < TOTEM_STATS.shatter.period || !cell || cell.phase !== 'idle') {
    ctx.ev.push({ type: 'shatter_denied', player, cell: index }); return;
  }
  const { kind, level } = cell;
  touchCell(ctx, player, index);
  cell.phase = 'empty'; cell.level = 0; cell.castTarget = -1; cell.castLeft = 0;
  p.totemCharge = 0;
  ctx.ev.push({ type: 'shatter', player, cell: index, kind, level });
}

export function stepBoards(ctx: Ctx) {
  const { st } = ctx, dt = DT;
  st.players.forEach((p, player) => {
    p.cells.forEach((cell, index) => {
      if (cell.phase === 'idle' || cell.phase === 'empty') return;
      if ((cell.timer -= dt) > 1e-9) return;
      const phase = cell.phase;
      if (phase === 'incoming') {
        placeRelic(st.rng, cell, cell.kind, 1);
        ctx.ev.push({ type: 'land', player, cell: index, kind: cell.kind });
      } else {
        cell.phase = 'idle'; cell.timer = 0; cell.version++;
        ctx.ev.push({ type: phase === 'merging' ? 'merged' : 'ascended', player, cell: index, kind: cell.kind, level: cell.level });
      }
    });
    const period = TOTEM_STATS[p.totem].period;
    if (period) {
      p.totemCharge = Math.min(period, p.totemCharge + dt);
      if (p.totemCharge >= period && p.totem === 'ascend') ascend(ctx, player);
    }
    // passive trickle; the seal holds at most two summons' worth
    const cost = summonCost(p);
    p.sp = Math.min(cost * 2, p.sp + dt * SEAL.trickle);
  });
}
